import type { AnyRoute, AuthContext, ApiResponse } from "./types.ts";
import { initSentry, captureException, flush } from "./sentry.ts";

// ---------------------------------------------------------------------------
// serve() — wraps a route into a Request → Response handler
//
// Handles: CORS preflight, input parsing, Zod validation, auth extraction,
// error formatting. Works with Deno.serve, Bun.serve, or any fetch-based
// runtime.
// ---------------------------------------------------------------------------

const CORS_HEADERS: Record<string, string> = {
	"Access-Control-Allow-Origin": "*",
	"Access-Control-Allow-Methods": "POST, OPTIONS",
	"Access-Control-Allow-Headers": "Content-Type, Authorization, x-client-info, apikey",
	"Access-Control-Max-Age": "86400",
};

function jsonResponse<T>(body: ApiResponse<T>, status: number): Response {
	return new Response(JSON.stringify(body), {
		status,
		headers: { "Content-Type": "application/json", ...CORS_HEADERS },
	});
}

/**
 * Parse auth from the Authorization header.
 * Handles both "Bearer <token>" and raw token formats.
 */
function extractAuth(headers: Headers): AuthContext {
	const authHeader = headers.get("authorization");
	if (!authHeader) return { token: null, claims: null };

	const token = authHeader.startsWith("Bearer ")
		? authHeader.slice(7)
		: authHeader;

	// Decode JWT claims (middle segment). No verification — Supabase
	// handles verification at the gateway level.
	let claims: Record<string, unknown> | null = null;
	try {
		const parts = token.split(".");
		if (parts.length === 3) {
			const payload = parts[1];
			const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
			claims = JSON.parse(decoded);
		}
	} catch {
		// Malformed token — claims stays null
	}

	return { token, claims };
}

/**
 * Extract raw input from the request body (POST only).
 */
async function extractInput(req: Request): Promise<unknown> {
	try {
		return await req.json();
	} catch {
		return {};
	}
}

/**
 * Collect headers into a plain object.
 */
function headersToObject(headers: Headers): Record<string, string> {
	const obj: Record<string, string> = {};
	headers.forEach((value, key) => {
		obj[key] = value;
	});
	return obj;
}

/**
 * Wraps a trellis route into a fetch-compatible request handler.
 *
 * @example
 * ```ts
 * // supabase/functions/store-get/index.ts
 * import { defineQuery, serve } from '@fliisi/trellis'
 *
 * export const fn = defineQuery({ ... })
 * Deno.serve(serve(fn))
 * ```
 */
export function serve(route: AnyRoute, options?: { name?: string }): (req: Request) => Promise<Response> {
	// Initialize Sentry once (no-op if SENTRY_DSN not set)
	const sentryReady = initSentry();

	return async (req: Request): Promise<Response> => {
		// CORS preflight
		if (req.method === "OPTIONS") {
			return new Response(null, { status: 204, headers: CORS_HEADERS });
		}

		// POST only
		if (req.method !== "POST") {
			return jsonResponse(
				{
					data: null,
					error: {
						code: "VALIDATION_ERROR",
						message: `Method ${req.method} not allowed. Use POST.`,
					},
				},
				405,
			);
		}

		let rawInput: unknown;
		let auth: AuthContext = { token: null, claims: null };

		try {
			// Extract input
			rawInput = await extractInput(req);

			// Validate with Zod
			const parsed = route.input.safeParse(rawInput);
			if (!parsed.success) {
				return jsonResponse(
					{
						data: null,
						error: {
							code: "VALIDATION_ERROR",
							message: "Invalid input",
							issues: parsed.error.issues.map((i) => ({
								path: i.path,
								message: i.message,
							})),
						},
					},
					400,
				);
			}

			// Build context
			auth = extractAuth(req.headers);
			const ctx = {
				auth,
				headers: headersToObject(req.headers),
			};

			// Execute handler
			const result = await route.handler({ input: parsed.data, ctx });

			return jsonResponse({ data: result, error: null }, 200);
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Unknown error";

			// Check for known error patterns
			if (message.includes("not found") || message.includes("No ")) {
				return jsonResponse(
					{
						data: null,
						error: { code: "NOT_FOUND", message },
					},
					404,
				);
			}

			// Report to Sentry (no-op if not configured)
			captureException(err, {
				route: options?.name,
				input: rawInput,
				auth,
			});
			await flush();

			return jsonResponse(
				{
					data: null,
					error: { code: "INTERNAL_ERROR", message },
				},
				500,
			);
		}
	};
}
